import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { postRequestAuthenticated } from "../../../services/rest";
import { getAppPrefix } from "../utility/embedHelper";
import { setEmbedItem } from "../embedSlices/embedHomeSlice";
import { FiSend, FiX, FiCpu } from "react-icons/fi";

// Kickoff prompts per proactive-trigger kind — the bot turns these into a
// short greeting. Manual opens (from the @mention) use DEFAULT_SEED.
const SEEDS = {
  welcome:
    "The member just joined the community. Greet them warmly in ONE short line and offer to point them somewhere.",
  reengage:
    "The member is back after a few days away. Welcome them back in ONE short line and ask if they need anything.",
  firstpost:
    "The member just made their first post in the community. Congratulate them in ONE short line and offer to point them somewhere.",
};
const DEFAULT_SEED =
  "The member just opened the assistant. Greet them briefly in one line.";

// Bot Studio community bot. Summoned from the chat @mention list, or opened by a
// proactive trigger (welcome / re-engage / first-post) — both set
// embedHome.botChatOpen (+ botTrigger). Chats via /bot/message; the bot may
// attach ONE verified action, which the member taps to perform (suggest-only).
const BotChat = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const username = useSelector((s) => s.embedHome?.username);
  const bot = useSelector((s) => s.embedHome?.bot) || {};
  const open = useSelector((s) => s.embedHome?.botChatOpen) || false;
  const botTrigger = useSelector((s) => s.embedHome?.botTrigger) || null;

  const [messages, setMessages] = useState([]); // {role:"bot"|"you", text, action?}
  const [history, setHistory] = useState([]); // {role:"model"|"user", text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const startedRef = useRef(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, busy]);

  // On load, ask the server whether a proactive trigger should fire (welcome for
  // first-timers, re-engage after ~5 idle days) and open the bot with it.
  useEffect(() => {
    if (!localStorage.getItem("auth-token")) return;
    let active = true;
    postRequestAuthenticated("/bot/trigger-check", {})
      .then((res) => {
        if (active && res?.success && res.show) {
          dispatch(setEmbedItem({ field: "botTrigger", value: res.kind }));
          dispatch(setEmbedItem({ field: "botChatOpen", value: true }));
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => dispatch(setEmbedItem({ field: "botChatOpen", value: false }));

  // Perform the tapped action. suggest_channel + open_topic deep-link cleanly;
  // open_events + take_to_support have no dedicated embed route yet, so they
  // land on the community home as a best-effort. FIXME(bot): wire a real events
  // view + auto-open "Talk to us" once those destinations exist.
  const act = (action) => {
    if (!action || !username) return;
    const base = `${getAppPrefix()}/user/${username}`;
    if (action.type === "suggest_channel" && action.channel) {
      const url = `${base}/channel/${action.channel._id}`;
      navigate(action.mode === "join" ? `${url}?autojoin=true` : url);
    } else if (action.type === "open_topic" && action.channel && action.topic) {
      navigate(
        `${base}/channel/${action.channel._id}/c-id/topic/${action.topic._id}`
      );
    } else if (action.type === "open_events" && action.channel) {
      navigate(`${base}/channel/${action.channel._id}`);
    } else if (action.type === "take_to_support") {
      // Signal the chat composer to open "Talk to us" (isBrandTalk). Timestamp
      // so repeated hand-offs re-trigger the watcher.
      dispatch(setEmbedItem({ field: "openTalkToUs", value: Date.now() }));
    }
    close();
  };

  const actionLabel = (a) => {
    if (a.type === "suggest_channel")
      return `${a.mode === "join" ? "Join" : "Open"} #${a.channel?.name}`;
    if (a.type === "open_topic") return `Open ${a.topic?.name}`;
    if (a.type === "open_events") return "See events";
    if (a.type === "take_to_support") return "Talk to the team";
    return "Open";
  };
  // Only these action types have somewhere to go from here.
  const actionable = (a) =>
    a &&
    ((a.type === "suggest_channel" && a.channel) ||
      (a.type === "open_topic" && a.channel && a.topic) ||
      (a.type === "open_events" && a.channel) ||
      a.type === "take_to_support");

  const send = async (message, isKickoff = false) => {
    if (busy) return;
    setBusy(true);
    const nextHistory = [...history];
    if (!isKickoff) {
      setMessages((m) => [...m, { role: "you", text: message }]);
      nextHistory.push({ role: "user", text: message });
    }
    try {
      const res = await postRequestAuthenticated("/bot/message", {
        history: nextHistory,
        message,
      });
      if (res?.success) {
        setMessages((m) => [
          ...m,
          { role: "bot", text: res.reply, action: res.action || null },
        ]);
        setHistory([...nextHistory, { role: "model", text: res.reply }]);
      } else {
        setMessages((m) => [
          ...m,
          { role: "bot", text: res?.message || "Let's try that again." },
        ]);
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "bot", text: "Something went wrong — try again." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  // Fresh greeting each time the panel opens; reset on close.
  useEffect(() => {
    if (open && !startedRef.current) {
      startedRef.current = true;
      send(SEEDS[botTrigger] || DEFAULT_SEED, true);
    }
    if (!open) {
      startedRef.current = false;
      setMessages([]);
      setHistory([]);
      setInput("");
      if (botTrigger) dispatch(setEmbedItem({ field: "botTrigger", value: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSend = () => {
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    send(msg);
  };

  // Render **bold** (the bot emphasizes channel names this way) as accent-colored
  // bold, so it shows as color instead of literal asterisks.
  const renderText = (text) =>
    (text || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i} className="font-semibold text-theme-accent">
          {part.slice(2, -2)}
        </strong>
      ) : (
        part
      )
    );

  if (!open) return null;

  return (
    <div className="absolute bottom-4 right-4 z-40 flex h-[34rem] max-h-[calc(100%-2rem)] w-[27rem] max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground shadow-2xl">
      {/* header */}
      <div className="flex items-center justify-between border-b border-theme-chatDivider px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-theme-accent text-theme-accentText">
            <FiCpu size={14} />
          </span>
          <span className="text-sm font-semibold text-theme-secondaryText">
            @{bot.name || "assistant"}
          </span>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="rounded-md p-1 text-theme-placeholder transition-colors hover:bg-theme-tertiaryBackground hover:text-theme-secondaryText"
        >
          <FiX size={18} />
        </button>
      </div>

      {/* messages */}
      <div
        ref={scrollRef}
        className="flex-1 space-y-2 overflow-y-auto px-3 py-3 custom-scrollbar"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex flex-col ${
              m.role === "you" ? "items-end" : "items-start"
            }`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                m.role === "you"
                  ? "rounded-br-sm bg-theme-accent text-theme-accentText"
                  : "rounded-bl-sm bg-theme-tertiaryBackground text-theme-secondaryText"
              }`}
            >
              {m.role === "bot" ? renderText(m.text) : m.text}
            </div>
            {m.role === "bot" && actionable(m.action) && (
              <button
                type="button"
                onClick={() => act(m.action)}
                className="mt-1.5 rounded-full bg-theme-accent px-3.5 py-1.5 text-xs font-semibold text-theme-accentText transition-opacity hover:opacity-90"
              >
                {actionLabel(m.action)}
              </button>
            )}
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-theme-tertiaryBackground px-3 py-2">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-theme-placeholder [animation-delay:-0.2s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-theme-placeholder [animation-delay:-0.1s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-theme-placeholder" />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* composer */}
      <div className="flex items-center gap-2 border-t border-theme-chatDivider px-3 py-2.5">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          disabled={busy}
          placeholder="Ask me anything about the community…"
          className="w-full rounded-full border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder disabled:opacity-60"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={busy || !input.trim()}
          className="flex-shrink-0 rounded-full bg-theme-accent p-2 text-theme-accentText transition-opacity hover:opacity-90 disabled:opacity-40"
          aria-label="Send"
        >
          <FiSend size={16} />
        </button>
      </div>
    </div>
  );
};

export default BotChat;
